import { useState, useEffect, useRef } from 'react';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import PersonaSelector from './components/PersonaSelector';
import Settings from './components/Settings';
import TypingIndicator from './components/TypingIndicator';
import ToolExecutionPanel from './components/ToolExecutionPanel';
import ConversationList from './components/ConversationList';
import Login from './components/Login';
import { apiFetch, getApiEndpoint, getWebSocketEndpoint } from './utils/apiClient';
import { getWebSocketClient, closeWebSocket } from './utils/websocketClient';
import { whoami, logout as authLogout } from './utils/auth';
import {
  createConversation,
  getUserConversations,
  getConversationWithMessages,
  deleteConversation
} from './utils/conversationApi';

function App() {
  // Auth gate via Cognito HttpOnly cookie.
  //   - On mount we call /auth/me (cookie-authenticated). 200 ⇒ logged in,
  //     401 ⇒ render <Login/> which bounces the user to the Cognito
  //     Hosted UI.
  //   - The api_key is no longer baked into the JS bundle and the JWT is
  //     never readable from JS (HttpOnly cookie). See utils/auth.js.
  const [authState, setAuthState] = useState({ status: 'checking', user: null });

  useEffect(() => {
    let cancelled = false;
    whoami().then((user) => {
      if (cancelled) return;
      setAuthState(user
        ? { status: 'authenticated', user }
        : { status: 'unauthenticated', user: null });
    });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    closeWebSocket();
    await authLogout();
    // authLogout() does a hard navigate to /auth/logout; reaching here
    // would be unusual, but flip the gate anyway.
    setAuthState({ status: 'unauthenticated', user: null });
  };

  const [selectedPersona, setSelectedPersona] = useState('engineer');
  const [showSettings, setShowSettings] = useState(false);
  const [customPersonas, setCustomPersonas] = useState({});
  
  // Per-conversation state map - replaces global isTyping/isStreaming/currentTool
  const [conversationStates, setConversationStates] = useState({}); // { conv_id: { isTyping, isStreaming, currentTool } }
  
  // UI state (reflects currently viewed conversation)
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  
  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState('New Conversation');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationTokens, setConversationTokens] = useState({ total: 0, input: 0, output: 0 });
  
  const [conversationItems, setConversationItems] = useState([ // Track messages and tool executions in order
    {
      id: 1,
      type: 'message',
      text: "Hello! I'm your AI assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const messagesEndRef = useRef(null);
  const personaSelectorRef = useRef(null);
  const currentConversationIdRef = useRef(null); // Track current conversation ID for filtering
  
  // Debug: Console log capture for Playwright MCP verification
  useEffect(() => {
    if (!globalThis.__consoleLogs) {
      globalThis.__consoleLogs = [];
      const originalLog = console.log;
      console.log = function(...args) {
        globalThis.__consoleLogs.push(args.map(a => 
          typeof a === 'object' ? JSON.stringify(a) : String(a)
        ).join(' '));
        if (globalThis.__consoleLogs.length > 1000) globalThis.__consoleLogs.shift();
        originalLog.apply(console, args);
      };
    }
    if (!globalThis.__handlerRegistry) {
      globalThis.__handlerRegistry = {};
    }
  }, []);
  
  // Helper: Update conversation-scoped state
  const updateConversationState = (convId, updates) => {
    setConversationStates(prev => ({
      ...prev,
      [convId]: { ...prev[convId], ...updates }
    }));
    console.log('[STATE:' + convId + ']', 'Updated state:', updates);
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationItems]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Load user's conversations
  const loadConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const data = await getUserConversations();
      const conversations = data.conversations || [];
      console.log('[TOKEN DEBUG] Loaded conversations:', conversations.map(c => ({
        id: c.conversation_id,
        title: c.title,
        tokens: c.total_tokens || 0
      })));
      setConversations(conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Create new conversation
  const handleNewConversation = async () => {
    // Clear UI state (viewed conversation state will be synced below)
    setIsTyping(false);
    setIsStreaming(false);
    setCurrentTool(null);
    
    try {
      const newConv = await createConversation();  // No title - let backend generate it
      setCurrentConversationId(newConv.conversation_id);
      currentConversationIdRef.current = newConv.conversation_id; // Update ref
      setConversationTitle(newConv.title || 'New Conversation');
      console.log('[TOKEN DEBUG] New conversation created, tokens reset to 0');
      setConversationTokens({ total: 0, input: 0, output: 0 });
      setConversationItems([
        {
          id: 1,
          type: 'message',
          text: "Hello! I'm your AI assistant. How can I help you today?",
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
      await loadConversations(); // Refresh list
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  // Select existing conversation
  const handleSelectConversation = async (conversationId) => {
    if (conversationId === currentConversationId) return;
    
    console.log('[UI]', 'Switching conversation', { from: currentConversationId, to: conversationId });
    
    // Load per-conversation state for the new conversation
    const convState = conversationStates[conversationId] || { 
      isTyping: false, 
      isStreaming: false, 
      currentTool: null 
    };
    setIsTyping(convState.isTyping);
    setIsStreaming(convState.isStreaming);
    setCurrentTool(convState.currentTool);
    
    try {
      const data = await getConversationWithMessages(conversationId);
      setCurrentConversationId(conversationId);
      currentConversationIdRef.current = conversationId; // Update ref for filtering
      setConversationTitle(data.title || 'New Conversation');
      
      // Update token counts
      const tokens = {
        total: data.total_tokens || 0,
        input: data.total_input_tokens || 0,
        output: data.total_output_tokens || 0
      };
      console.log('[TOKEN DEBUG] Selected conversation:', conversationId, 'tokens:', tokens);
      setConversationTokens(tokens);
      
      // Convert messages to conversation items
      // Use the message's actual ID from backend to prevent duplicates
      const items = data.messages.map((msg) => ({
        id: msg.message_id || msg._id || `msg_${Date.now()}_${Math.random()}`,
        type: 'message',
        text: Array.isArray(msg.content) 
          ? msg.content.map(c => c.text).join('\n')
          : msg.content,
        sender: msg.role === 'user' ? 'user' : 'bot',
        timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }));
      
      setConversationItems(items.length > 0 ? items : [
        {
          id: 1,
          type: 'message',
          text: "Hello! I'm your AI assistant. How can I help you today?",
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId) => {
    if (!confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      await deleteConversation(conversationId);
      
      // If we deleted current conversation, create new one
      if (conversationId === currentConversationId) {
        await handleNewConversation();
      }
      
      await loadConversations(); // Refresh list
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Rename conversation
  const handleRenameConversation = async (conversationId, newTitle) => {
    try {
      const response = await apiFetch(`/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to rename conversation: ${response.status}`);
      }
      
      // Update local state
      if (conversationId === currentConversationId) {
        setConversationTitle(newTitle);
      }
      
      await loadConversations(); // Refresh list
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    // Ensure we have a conversation
    let conversationId = currentConversationId;
    if (!conversationId) {
      try {
        const newConv = await createConversation();
        conversationId = newConv.conversation_id;
        setCurrentConversationId(conversationId);
        currentConversationIdRef.current = conversationId; // CRITICAL: Set ref immediately!
        setConversationTitle(newConv.title || 'New Conversation');
        await loadConversations();
      } catch (error) {
        console.error('Error creating conversation:', error);
        return;
      }
    }

    // Add user message to UI
    const userMessage = {
      id: Date.now(),
      type: 'message',
      text: text,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setConversationItems(prev => [...prev, userMessage]);
    
    // DON'T save user message here - let backend save it with the response
    // This prevents duplication when backend loads conversation history
    
    // Show typing indicator
    updateConversationState(conversationId, { isTyping: true, isStreaming: false, currentTool: null });
    setIsTyping(true);

    try {
      // Get WebSocket endpoint (uses VITE_WS_ENDPOINT or derives from API endpoint)
      const endpoint = getWebSocketEndpoint();
      
      // Use WebSocket if endpoint is ws:// or wss://
      const useWebSocket = endpoint.startsWith('ws://') || endpoint.startsWith('wss://');
      
      if (useWebSocket) {
        // WebSocket mode (local or production)
        const wsClient = getWebSocketClient(endpoint);
        
        let currentMessageText = '';
        let currentMessageId = Date.now() + 1;
        let hasStartedStreaming = false;
        
        // Capture the conversation ID at handler creation (immutable)
        const handlerConversationId = conversationId;
        // Generate unique request ID for this specific message
        const handlerRequestId = `${conversationId}_${Date.now()}_${Math.random()}`;
        
        // Register handler
        globalThis.__handlerRegistry[handlerConversationId] = (globalThis.__handlerRegistry[handlerConversationId] || 0) + 1;
        console.log('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Created handler', { total_handlers: Object.keys(globalThis.__handlerRegistry).length });
        
        console.log('[DEBUG] Calling invokeAgent with:', {
          agentId: selectedPersona,
          prompt: text,
          requestId: handlerRequestId,
          conversationId: handlerConversationId
        });
        
        await wsClient.invokeAgent(
          selectedPersona,
          text,
          handlerRequestId,  // request ID for tracking
          // onMessage - handle streaming chunks and tool events
          (data) => {
            console.log('[DEBUG] Message received:', {
              type: data.type,
              conversation_id: data.conversation_id,
              request_id: data.request_id,
              handler_conv: handlerConversationId,
              handler_req: handlerRequestId
            });
            
            // CRITICAL: Filter by handler's OWN conversation_id and request_id
            if (!data.conversation_id) {
              console.error('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Message missing conversation_id!', data);
              return;
            }
            if (data.conversation_id !== handlerConversationId) {
              console.log('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Ignoring message for different conversation:', data.conversation_id);
              return;
            }
            // Only filter by request_id if present (backend may not support it yet)
            if (data.request_id && data.request_id !== handlerRequestId) {
              console.log('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Ignoring message for different request:', data.request_id);
              return;
            }
            
            console.log('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Processing', data.type, { 
              current_view: currentConversationIdRef.current,
              handler_conv: handlerConversationId,
              matches: currentConversationIdRef.current === handlerConversationId 
            });
            
            if (data.type === 'message') {
              // First chunk - switch from thinking to streaming
              if (!hasStartedStreaming) {
                hasStartedStreaming = true;
                updateConversationState(handlerConversationId, { isTyping: false, isStreaming: true });
                // Update UI if viewing this conversation
                if (currentConversationIdRef.current === handlerConversationId) {
                  setIsTyping(false);
                  setIsStreaming(true);
                }
              }
              
              currentMessageText += data.content;
              
              // Only update UI if viewing this conversation
              if (currentConversationIdRef.current === handlerConversationId) {
                setConversationItems(prev => {
                  const existing = prev.find(m => m.id === currentMessageId);
                  if (existing) {
                    return prev.map(m => 
                      m.id === currentMessageId 
                        ? { ...m, text: currentMessageText }
                        : m
                    );
                  } else {
                    return [...prev, {
                      id: currentMessageId,
                      type: 'message',
                      text: currentMessageText,
                      sender: 'bot',
                      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }];
                  }
                });
              }
            } else if (data.type === 'tool_start') {
              // Tool execution started - split message here
              // If we have accumulated text, finalize current message
              if (currentMessageText.trim()) {
                // Only update UI if viewing this conversation
                if (currentConversationIdRef.current === handlerConversationId) {
                  setConversationItems(prev => {
                    const existing = prev.find(m => m.id === currentMessageId);
                    if (existing) {
                      return prev.map(m => 
                        m.id === currentMessageId 
                          ? { ...m, text: currentMessageText, finalized: true }
                          : m
                      );
                    }
                    return prev;
                  });
                }
                // Reset for next message block
                currentMessageText = '';
                currentMessageId = Date.now() + Math.random();
              }
              
              // Add tool execution item
              const executionId = Date.now() + Math.random() * 1000;
              updateConversationState(handlerConversationId, { currentTool: data.tool_name });
              // Update UI if viewing this conversation
              if (currentConversationIdRef.current === handlerConversationId) {
                setCurrentTool(data.tool_name);
                setConversationItems(prev => [...prev, {
                  id: executionId,
                  type: 'tool',
                  toolName: data.tool_name,
                  parameters: data.parameters || {},
                  status: 'executing',
                  timestamp: new Date(),
                  startTime: Date.now()
                }]);
              }
              console.log(`Tool started: ${data.tool_name}`, data.parameters);
            } else if (data.type === 'tool_end') {
              // Tool execution completed - update status
              updateConversationState(handlerConversationId, { currentTool: null });
              // Update UI if viewing this conversation
              if (currentConversationIdRef.current === handlerConversationId) {
                setCurrentTool(null);
                setConversationItems(prev => {
                  // Find the most recent executing tool
                  const lastToolIndex = [...prev].reverse().findIndex(item => 
                    item.type === 'tool' && item.status === 'executing'
                  );
                  
                  if (lastToolIndex !== -1) {
                    const actualIndex = prev.length - 1 - lastToolIndex;
                    return prev.map((item, idx) => 
                      idx === actualIndex
                        ? {
                            ...item,
                            status: data.status || 'success',
                            result: data.result,
                            error: data.error,
                            duration: Date.now() - item.startTime
                          }
                        : item
                    );
                  }
                  return prev;
                });
              }
              console.log(`Tool completed: ${data.tool_name}`, data.status);
            }
          },
          // onError
          (error) => {
            console.error('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'WebSocket error:', error);
            
            // Cleanup handler registry
            if (globalThis.__handlerRegistry[handlerConversationId]) {
              globalThis.__handlerRegistry[handlerConversationId]--;
              if (globalThis.__handlerRegistry[handlerConversationId] === 0) {
                delete globalThis.__handlerRegistry[handlerConversationId];
              }
            }
            console.log('[HANDLER:' + handlerConversationId + ']', 'Cleaned up on error', { remaining: Object.keys(globalThis.__handlerRegistry).length });
            
            updateConversationState(handlerConversationId, { isTyping: false, isStreaming: false, currentTool: null });
            // Update UI if viewing this conversation
            if (currentConversationIdRef.current === handlerConversationId) {
              setIsTyping(false);
              setIsStreaming(false);
              setCurrentTool(null);
              
              // Mark any executing tool as failed
              setConversationItems(prev => 
                prev.map(item => 
                  item.type === 'tool' && item.status === 'executing'
                    ? { ...item, status: 'error', error: error.message, duration: Date.now() - item.startTime }
                    : item
                )
              );
              
              const errorMessage = {
                id: Date.now() + 2,
                type: 'message',
                text: `Sorry, I encountered an error: ${error.message}. Please make sure the backend is running.`,
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              };
              
              setConversationItems(prev => [...prev, errorMessage]);
            }
          },
          // onComplete
          async () => {
            console.log('[HANDLER:' + handlerConversationId + '][REQ:' + handlerRequestId + ']', 'Completed');
            
            // Cleanup handler registry
            if (globalThis.__handlerRegistry[handlerConversationId]) {
              globalThis.__handlerRegistry[handlerConversationId]--;
              if (globalThis.__handlerRegistry[handlerConversationId] === 0) {
                delete globalThis.__handlerRegistry[handlerConversationId];
              }
            }
            console.log('[HANDLER:' + handlerConversationId + ']', 'Cleaned up on completion', { remaining: Object.keys(globalThis.__handlerRegistry).length });
            
            updateConversationState(handlerConversationId, { isTyping: false, isStreaming: false, currentTool: null });
            // Update UI if viewing this conversation
            if (currentConversationIdRef.current === handlerConversationId) {
              setIsTyping(false);
              setIsStreaming(false);
              setCurrentTool(null);
            }
            // Refresh conversations to update message count and timestamp
            await loadConversations();
            // Reload current conversation to update token counts in header
            if (conversationId) {
              try {
                console.log('[TOKEN DEBUG] Reloading conversation after completion:', conversationId);
                const data = await getConversationWithMessages(conversationId);
                const oldTokens = { ...conversationTokens };
                const newTokens = {
                  total: data.total_tokens || 0,
                  input: data.total_input_tokens || 0,
                  output: data.total_output_tokens || 0
                };
                console.log('[TOKEN DEBUG] Token update:', {
                  before: oldTokens,
                  after: newTokens,
                  delta: {
                    total: newTokens.total - oldTokens.total,
                    input: newTokens.input - oldTokens.input,
                    output: newTokens.output - oldTokens.output
                  }
                });
                setConversationTokens(newTokens);
              } catch (error) {
                console.error('Error reloading conversation tokens:', error);
              }
            }
          },
          // conversation_id
          conversationId
        );
      } else {
        // HTTP mode (local development)
        const response = await apiFetch('/invocations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            input: {
              prompt: text
            },
            agent_id: selectedPersona
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Hide typing indicator and add bot response
        setIsTyping(false);
        const botMessage = {
          id: Date.now() + 1,
          type: 'message',
          text: data.output.message.content,
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        
        if (currentConversationIdRef.current === conversationId) {
          setConversationItems(prev => [...prev, botMessage]);
        }
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Hide typing indicator and add error message
      setIsTyping(false);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'message',
        text: `Sorry, I encountered an error: ${error.message}. Please make sure the backend is running on ${getApiEndpoint()}`,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      if (currentConversationIdRef.current === conversationId) {
        setConversationItems(prev => [...prev, errorMessage]);
      }
    }
  };

  const handlePersonaChange = (persona) => {
    setSelectedPersona(persona);
    // Add a system message when persona changes
    const now = new Date();
    const systemMessage = {
      id: now.getTime(),
      type: 'message',
      text: `Switched to ${persona} persona. I'll now respond as a ${getPersonaDescription(persona)}.`,
      sender: 'system',
      timestamp: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    if (currentConversationIdRef.current) {
      setConversationItems(prev => [...prev, systemMessage]);
    }
  };

  const getPersonaDescription = (persona) => {
    switch(persona.toLowerCase()) {
      case 'po': return 'Product Owner - focused on business requirements and user needs';
      case 'architect': return 'Software Architect - focused on system design and technical decisions';
      case 'engineer': return 'Software Engineer - focused on implementation and code quality';
      default: return 'AI Assistant';
    }
  };

  const handleUpdatePersona = (personaId, updatedPersona) => {
    setCustomPersonas(prev => ({
      ...prev,
      [personaId]: updatedPersona
    }));
    
    // Add a system message when persona is updated
    const systemMessage = {
      id: Date.now() + Math.random(),
      type: 'message',
      text: `✨ ${personaId} persona has been updated with new instructions.`,
      sender: 'system',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    if (currentConversationIdRef.current) {
      setConversationItems(prev => [...prev, systemMessage]);
    }
  };

  // Bootstrap auth state. Show a tiny placeholder while /auth/me resolves
  // so the chat UI doesn't flash before redirecting unauthenticated users.
  if (authState.status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-gray-400 text-sm">
        Checking session…
      </div>
    );
  }
  if (authState.status === 'unauthenticated') {
    return <Login />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Conversation List Sidebar */}
      <ConversationList
        conversations={conversations}
        currentConversationId={currentConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
        onOpenSettings={() => setShowSettings(true)}
        onLogout={handleLogout}
        isLoading={isLoadingConversations}
      />
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col" data-conversation={currentConversationId}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              {isEditingTitle ? (
                <input
                  type="text"
                  value={editingTitleValue}
                  onChange={(e) => setEditingTitleValue(e.target.value)}
                  onBlur={() => {
                    if (editingTitleValue.trim() && editingTitleValue !== conversationTitle && currentConversationId) {
                      handleRenameConversation(currentConversationId, editingTitleValue);
                    }
                    setIsEditingTitle(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (editingTitleValue.trim() && editingTitleValue !== conversationTitle && currentConversationId) {
                        handleRenameConversation(currentConversationId, editingTitleValue);
                      }
                      setIsEditingTitle(false);
                    } else if (e.key === 'Escape') {
                      setIsEditingTitle(false);
                    }
                  }}
                  className="text-xl font-semibold text-gray-800 bg-gray-50 px-3 py-1 rounded border-2 border-blue-500 focus:outline-none w-full max-w-xl"
                  autoFocus
                />
              ) : (
                <h1 
                  className="text-xl font-semibold text-gray-800 cursor-text hover:bg-gray-50 px-3 py-1 rounded inline-block"
                  onDoubleClick={() => {
                    if (currentConversationId) {
                      setIsEditingTitle(true);
                      setEditingTitleValue(conversationTitle);
                    }
                  }}
                  title="Double-click to edit"
                >
                  {conversationTitle}
                </h1>
              )}
              <p className="text-sm text-gray-500">
                Your AI assistant
              </p>
            </div>
            <div className="flex items-center space-x-6">
              {conversationTokens.total > 0 && (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <div className="text-xs">
                    <span className="font-semibold text-blue-700">{conversationTokens.total.toLocaleString()}</span>
                    <span className="text-blue-600 ml-1">tokens</span>
                  </div>
                  <div className="text-xs text-blue-500 border-l border-blue-300 pl-2">
                    ↑{conversationTokens.input.toLocaleString()} ↓{conversationTokens.output.toLocaleString()}
                  </div>
                </div>
              )}
              <label className="text-sm text-gray-600 font-medium">Agent:</label>
              <select
                value={selectedPersona}
                onChange={(e) => handlePersonaChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors cursor-pointer"
              >
                <option value="engineer">⚙️ Engineer</option>
                <option value="architect">🏗️ Architect</option>
                <option value="po">📊 Product Owner</option>
                <option value="security-analyst">🔒 Security Analyst</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {conversationItems.map((item) => (
              item.type === 'message' ? (
                <ChatMessage key={item.id} message={item} />
              ) : (
                <ToolExecutionPanel key={item.id} toolExecution={item} />
              )
            ))}
            {/* Thinking/Streaming/Tool Indicators */}
            {isTyping && !currentTool && <TypingIndicator key="thinking" text="Thinking..." showDots={true} />}
            {currentTool && <TypingIndicator key="tool" text={`Using tool: ${currentTool}`} showDots={true} />}
            {isStreaming && !currentTool && <TypingIndicator key="typing" text="Typing..." showDots={true} />}
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <ChatInput onSendMessage={handleSendMessage} />
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <Settings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        personas={customPersonas}
        onUpdatePersona={handleUpdatePersona}
        onAgentsChanged={() => personaSelectorRef.current?.refreshAgents()}
      />
    </div>
  );
}

export default App;
